import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  PlayIcon,
  ArrowLeftIcon,
  ClockIcon,
  LightBulbIcon,
  ScaleIcon,
  ShieldExclamationIcon,
  ChartBarSquareIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { craApi } from '../lib/api';
import DataVintageBadge from '../components/cra/DataVintageBadge';
import ModuleConnections from '../components/help/ModuleConnections';
import { useTeamId } from '../hooks/useTeamId';

function getVisibleDimensions(bankCategory: string) {
  if (bankCategory === 'SMALL') return ['Overall', 'Lending'];
  if (bankCategory === 'LARGE') return ['Overall', 'Lending', 'Community Dev', 'Service'];
  return ['Overall', 'Lending', 'Community Dev']; // INTERMEDIATE
}

function GapScoreRadarChart({ analysis, bankCategory = 'INTERMEDIATE' }: { analysis: any; bankCategory?: string }) {
  const visible = getVisibleDimensions(bankCategory);
  const data = [
    { dimension: 'Overall', score: analysis.overallGapScore ?? 0 },
    { dimension: 'Lending', score: analysis.lendingGapScore ?? 0 },
    { dimension: 'Community Dev', score: analysis.cdGapScore ?? 0 },
    { dimension: 'Service', score: analysis.serviceGapScore ?? 0 },
  ].filter(d => visible.includes(d.dimension));

  return (
    <div className="flex justify-center mb-6">
      <ResponsiveContainer width="100%" height={280} minWidth={280}>
        <RadarChart data={data} cx="50%" cy="50%" outerRadius="75%">
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 12, fill: '#374151' }} />
          <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#9ca3af' }} tickCount={5} />
          <Radar
            dataKey="score"
            stroke="#0ea5e9"
            fill="#0ea5e9"
            fillOpacity={0.25}
            strokeWidth={2}
            dot={{ r: 4, fill: '#0ea5e9' }}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

function GapScoreTrendChart({ analyses, bankCategory = 'INTERMEDIATE' }: { analyses: any[]; bankCategory?: string }) {
  // De-duplicate by analysisYear — keep only the most recently completed
  // analysis per year so x-axis labels don't repeat.
  const byYear = new Map<number, any>();
  for (const a of analyses) {
    if (a.status !== 'COMPLETED') continue;
    const yr = a.analysisYear;
    if (yr == null) continue;
    const existing = byYear.get(yr);
    const aTime = new Date(a.completedAt || a.updatedAt || a.createdAt || 0).getTime();
    const eTime = existing ? new Date(existing.completedAt || existing.updatedAt || existing.createdAt || 0).getTime() : -1;
    if (!existing || aTime > eTime) byYear.set(yr, a);
  }
  const completed = Array.from(byYear.values()).sort(
    (a: any, b: any) => a.analysisYear - b.analysisYear
  );

  if (completed.length < 2) return null;

  const data = completed.map((a: any) => ({
    year: String(a.analysisYear),
    overall: a.overallGapScore ?? 0,
    lending: a.lendingGapScore ?? 0,
    cd: a.cdGapScore ?? 0,
    service: a.serviceGapScore ?? 0,
  }));

  const showCd = bankCategory !== 'SMALL';
  const showService = bankCategory === 'LARGE';

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Gap Score Trend</h3>
      <p className="text-xs text-gray-500 mb-4">Tracking gap scores across {completed.length} analyses</p>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <XAxis dataKey="year" tick={{ fontSize: 12 }} interval={0} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="overall" name="Overall" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 4 }} />
          <Line type="monotone" dataKey="lending" name="Lending" stroke="#2563eb" strokeWidth={2} dot={{ r: 4 }} />
          {showCd && <Line type="monotone" dataKey="cd" name="Community Dev" stroke="#16a34a" strokeWidth={2} dot={{ r: 4 }} />}
          {showService && <Line type="monotone" dataKey="service" name="Service" stroke="#d97706" strokeWidth={2} dot={{ r: 4 }} />}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function CraGapAnalysisPage() {
  const { teamId, isLoading: teamLoading } = useTeamId();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['cra-gap-analyses', teamId],
    queryFn: async () => {
      const res = await craApi.listGapAnalyses(teamId!);
      return res.data;
    },
    enabled: !!teamId,
    refetchInterval: (query) => {
      const analyses = query.state.data?.analyses;
      const hasRunning = analyses?.some((a: any) => a.status === 'PENDING' || a.status === 'RUNNING');
      return hasRunning ? 5000 : false;
    },
  });

  const { data: configData } = useQuery({
    queryKey: ['cra-config', teamId],
    queryFn: async () => {
      const res = await craApi.getConfig(teamId!);
      return res.data;
    },
    enabled: !!teamId,
  });

  // GET /cra/config returns the configuration row itself (not { config }).
  const bankCategory = configData?.bankCategory ?? 'INTERMEDIATE';
  const showCd = bankCategory !== 'SMALL';
  const showService = bankCategory === 'LARGE';

  const runMutation = useMutation({
    mutationFn: () => craApi.runGapAnalysis(teamId!, { analysisYear: new Date().getFullYear() }),
    onSuccess: () => {
      toast.success('Gap analysis started — this may take a minute');
      queryClient.invalidateQueries({ queryKey: ['cra-gap-analyses'] });
    },
    onError: () => toast.error('Failed to start gap analysis'),
  });

  if (teamLoading || isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-96 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  const analyses = data?.analyses || [];

  const getScoreColor = (score: number) => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score: number) => {
    if (score >= 75) return 'bg-green-50';
    if (score >= 50) return 'bg-amber-50';
    return 'bg-red-50';
  };

  return (
    <div>
      <div className="mb-6">
        <Link to="/cra" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to CRA Dashboard
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-navy-900">Gap Analysis</h1>
            <p className="mt-1 text-sm text-gray-500">Cross-module CRA gap identification with AI-powered recommendations</p>
          </div>
          <button
            onClick={() => runMutation.mutate()}
            disabled={runMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700 disabled:opacity-50"
          >
            <PlayIcon className="h-4 w-4" />
            {runMutation.isPending ? 'Starting...' : 'Run Gap Analysis'}
          </button>
        </div>
      </div>

      <ModuleConnections moduleKey="cra-gap-analysis" className="mb-6" />

      {/* Gap Score Trend */}
      <GapScoreTrendChart analyses={analyses} bankCategory={bankCategory} />

      {/* Analysis Results */}
      {analyses.length > 0 ? (
        <div className="space-y-6">
          {analyses.map((analysis: any) => (
            <div key={analysis.id} className="bg-white rounded-lg shadow">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {analysis.analysisYear} Gap Analysis
                  </h2>
                  {analysis.status === 'COMPLETED' ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
                      Complete
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                      <ClockIcon className="h-3 w-3" />
                      {analysis.status}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {analysis.dataVintage && <DataVintageBadge vintage={analysis.dataVintage} />}
                  <span className="text-sm text-gray-500">
                    {new Date(analysis.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {analysis.status === 'COMPLETED' && (
                <div className="p-6">
                  {/* Score Cards */}
                  <div className={`grid grid-cols-2 ${showCd && showService ? 'md:grid-cols-4' : showCd ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4 mb-6`}>
                    <div className={`rounded-lg p-4 ${getScoreBg(analysis.overallGapScore ?? 0)}`}>
                      <p className="text-xs text-gray-500">Overall Score</p>
                      <p className={`text-2xl font-bold tabular-nums ${getScoreColor(analysis.overallGapScore ?? 0)}`}>
                        {analysis.overallGapScore ?? 0}/100
                      </p>
                    </div>
                    <div className={`rounded-lg p-4 ${getScoreBg(analysis.lendingGapScore ?? 0)}`}>
                      <p className="text-xs text-gray-500">Lending</p>
                      <p className={`text-2xl font-bold tabular-nums ${getScoreColor(analysis.lendingGapScore ?? 0)}`}>
                        {analysis.lendingGapScore ?? 0}/100
                      </p>
                    </div>
                    {showCd && (
                      <div className={`rounded-lg p-4 ${getScoreBg(analysis.cdGapScore ?? 0)}`}>
                        <p className="text-xs text-gray-500">Community Dev</p>
                        <p className={`text-2xl font-bold tabular-nums ${getScoreColor(analysis.cdGapScore ?? 0)}`}>
                          {analysis.cdGapScore ?? 0}/100
                        </p>
                      </div>
                    )}
                    {showService && (
                      <div className={`rounded-lg p-4 ${getScoreBg(analysis.serviceGapScore ?? 0)}`}>
                        <p className="text-xs text-gray-500">Service</p>
                        <p className={`text-2xl font-bold tabular-nums ${getScoreColor(analysis.serviceGapScore ?? 0)}`}>
                          {analysis.serviceGapScore ?? 0}/100
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Radar Chart */}
                  <GapScoreRadarChart analysis={analysis} bankCategory={bankCategory} />

                  {/* AI Summary */}
                  {analysis.aiSummary && (
                    <div className="rounded-lg bg-ocean-50 border border-ocean-100 p-4 mb-6">
                      <div className="flex items-start gap-2">
                        <LightBulbIcon className="h-5 w-5 text-ocean-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-ocean-800 mb-1">AI Summary</p>
                          <p className="text-sm text-ocean-700 whitespace-pre-line">{analysis.aiSummary}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* AI Recommendations */}
                  {analysis.aiRecommendations?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Recommendations</h3>
                      <div className="space-y-3">
                        {analysis.aiRecommendations.map((rec: any, i: number) => (
                          <div key={i} className="rounded-lg border border-gray-200 p-4">
                            <div className="flex items-start justify-between mb-2">
                              <p className="text-sm font-medium text-gray-900">{rec.recommendation}</p>
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                                rec.priority === 'HIGH' ? 'bg-red-100 text-red-700' :
                                rec.priority === 'MEDIUM' ? 'bg-amber-100 text-amber-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {rec.priority}
                              </span>
                            </div>
                            {rec.impact && <p className="text-xs text-gray-500">Impact: {rec.impact}</p>}
                            {rec.effort && <p className="text-xs text-gray-500">Effort: {rec.effort}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Cross-Module Correlation Panel */}
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <ChartBarSquareIcon className="h-4 w-4 text-ocean-600" />
                      Cross-Module Correlations
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Redlining Overlaps */}
                      <div className="rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
                          <h4 className="text-sm font-medium text-gray-900">Redlining Overlaps</h4>
                        </div>
                        {analysis.redliningOverlaps?.length > 0 ? (
                          <ul className="text-xs text-gray-600 space-y-1">
                            {analysis.redliningOverlaps.slice(0, 5).map((item: any, i: number) => (
                              <li key={i}>{typeof item === 'string' ? item : item.tract || JSON.stringify(item)}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-gray-400">No overlaps detected</p>
                        )}
                      </div>

                      {/* Fair Lending Overlaps */}
                      <div className="rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <ScaleIcon className="h-4 w-4 text-amber-500" />
                          <h4 className="text-sm font-medium text-gray-900">Fair Lending Overlaps</h4>
                        </div>
                        {analysis.fairLendingOverlaps?.length > 0 ? (
                          <ul className="text-xs text-gray-600 space-y-1">
                            {analysis.fairLendingOverlaps.slice(0, 5).map((item: any, i: number) => (
                              <li key={i}>{typeof item === 'string' ? item : item.finding || JSON.stringify(item)}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-gray-400">No overlaps detected</p>
                        )}
                      </div>

                      {/* Enforcement Correlations */}
                      <div className="rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <ShieldExclamationIcon className="h-4 w-4 text-purple-500" />
                          <h4 className="text-sm font-medium text-gray-900">Enforcement Correlations</h4>
                        </div>
                        {analysis.enforcementCorrelations?.length > 0 ? (
                          <ul className="text-xs text-gray-600 space-y-1">
                            {analysis.enforcementCorrelations.slice(0, 5).map((item: any, i: number) => (
                              <li key={i}>{typeof item === 'string' ? item : item.action || JSON.stringify(item)}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-gray-400">No correlations found</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Risk Assessment */}
                  {analysis.aiRiskAssessment && (
                    <div className="mt-6 rounded-lg bg-gray-50 p-4">
                      <p className="text-xs font-medium text-gray-500 mb-1">AI Risk Assessment</p>
                      <p className="text-sm text-gray-700 whitespace-pre-line">{analysis.aiRiskAssessment}</p>
                    </div>
                  )}
                </div>
              )}

              {analysis.status === 'RUNNING' && (
                <div className="p-6 text-center">
                  <div className="animate-spin h-8 w-8 border-4 border-ocean-200 border-t-ocean-600 rounded-full mx-auto mb-3" />
                  <p className="text-sm text-gray-500">Analysis in progress... This may take 1-2 minutes.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-16 text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-base font-semibold text-navy-900">No Gap Analyses</h3>
            <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
              Run an AI-powered gap analysis to identify underserved areas, community development opportunities, and cross-module risk correlations across your CRA, fair lending, and redlining data.
            </p>
            <button
              onClick={() => runMutation.mutate()}
              disabled={runMutation.isPending}
              className="mt-4 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700 disabled:opacity-50"
            >
              <PlayIcon className="h-4 w-4" />
              Run First Analysis
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
